<template>
  <div class="avatarContentEditionPanel">
    <div class="avatarContentEditionContainer">
      <div class="avatarContentEditorTitle">
        {{ $t('aiAvatar.script') }}
        <b-dropdown aria-role="list" id="buttonPauseDropDown" class="pauseButtonContent">
          <template #trigger="{ active }">
            <w-button icon="timer-plus-outline"
                      size="small"
                      color="outlined"
                      :tooltip="$t('aiAvatar.tooltip.addPause')">
              {{ $t('aiAvatar.addPause') }}
            </w-button>
          </template>
          <b-dropdown-item aria-role="listitem"  @click="insertPauseTag(0.25)" class="lineButtonGenerate"><span class="labelButtonGenerate">{{ $t('aiAvatar.pause.extraWeak') }}</span></b-dropdown-item>
          <b-dropdown-item aria-role="listitem"  @click="insertPauseTag(0.5)" class="lineButtonGenerate"><span class="labelButtonGenerate">{{ $t('aiAvatar.pause.weak') }}</span></b-dropdown-item>
          <b-dropdown-item aria-role="listitem"  @click="insertPauseTag(0.75)" class="lineButtonGenerate"><span class="labelButtonGenerate">{{ $t('aiAvatar.pause.medium') }}</span></b-dropdown-item>
          <b-dropdown-item aria-role="listitem"  @click="insertPauseTag(1)" class="lineButtonGenerate"><span class="labelButtonGenerate">{{ $t('aiAvatar.pause.strong') }}</span></b-dropdown-item>
          <b-dropdown-item aria-role="listitem"  @click="insertPauseTag(1.5)" class="lineButtonGenerate"><span class="labelButtonGenerate">{{ $t('aiAvatar.pause.extraStrong') }}</span></b-dropdown-item>
          <w-divider/>
          <b-dropdown-item aria-role="listitem" class="lineButtonGenerate" >
            <div class="pauseCustomTitle">{{ $t('aiAvatar.pause.custom') }}</div>
            <div class="pauseCustomLine">
              <div @click.stop><w-input v-model.number="customPause" placeholder="3.5" size="small"/></div>
              <w-button @click="insertPauseTag(customPause)" size="small" color="outlined" :enabled="isCustomPauseValid">{{ $t('aiAvatar.pause.customAdd') }}</w-button>
            </div>
            <div v-show="!isCustomPauseValid">{{ $t('aiAvatar.pause.customError') }}</div>
          </b-dropdown-item>
        </b-dropdown>
      </div>

      <!-- INPUT TEXT -->
      <w-input type="textarea" v-model="textToGenerate" @input="onTextInput" class="contentTextAreaAI"
               :class="{overtext:textToGenerate.length>maxlengthOfCharacter}"
               :placeholder="$t('aiAvatar.content.placeholder')"
               :maxlength="maxlengthOfCharacter"></w-input>

      <div v-if="ssmlErrors !== ''" class="errorSSML">
        <w-error :message="ssmlErrors"/>
      </div>

      <!-- SELECT LANGUAGE -->
      <div class="lineSelect">
        <div class="labelSelect">{{ $t('aiAvatar.langsSelector') }}</div>
        <w-select class="langSelector" size="small" @input="onAvatarLangChange" :value="langSelected">
          <option :value="listOfVoicesLangs[0].value" :key="'voice_lang_'+listOfVoicesLangs[0].value">
            {{ listOfVoicesLangs[0].label }}
          </option>
          <hr>
          <option v-for="lang in listOfVoicesLangs.slice(1)" :value="lang.value" :key="'voice_lang_'+lang.value">
            {{ lang.label }}
          </option>
        </w-select>
      </div>

      <!-- SELECT VOICES -->
      <div class="lineSelect">
        <div class="labelSelect labelVoiceSelector">{{ $t('aiAvatar.voicesSelector') }}</div>
        <w-select class="voiceSelector" size="small" :value="voiceSelected" @input="onAvatarVoiceChange">
          <option v-for="voice in listOfVoice" :value="voice.value" :key="'voice_'+voice.value">
            {{ voice.label }}
          </option>
        </w-select>

        <w-button :icon="playingVoice?'stop':'volume-high'" :enabled="textToGenerate && !ssmlErrors"
                  @click="previewVoice" :tooltip="$t('aiAvatar.tooltip.previewVoice')"
                  class="buttonPlayPreview" size="small" color="outlined"
                  :loading="loadingVoice"></w-button>
      </div>


      <div class="lineButton" v-if="slides.length > 0">
        <b-dropdown aria-role="list"  id="buttonDropDown">
          <template #trigger="{ active }">
            <w-button class="buttonOneSlide" :expanded="true" :icon-right="active ? 'menu-up' : 'menu-down'"  :enabled="!!textToGenerate && !ssmlErrors"
                      :loading="isAvatarGenerating || loadingVoice"
                      color="primary" size="small">
              {{$t("aiAvatar.generate")}}
            </w-button>
          </template>
          <b-dropdown-item aria-role="listitem"  @click="generateSlide" class="lineButtonGenerate"><w-icon class="iconButtonsGenerate" :icon="selectedSegment?'file-replace-outline':'play-box-outline'"/>
            <span class="labelButtonGenerate" v-if="!selectedSegment">
            {{$t('aiAvatar.generateThisSlide')}}
            </span>
          <span v-else>
             {{ $t("aiAvatar.replaceThisSlide")}}
          </span></b-dropdown-item>
          <b-dropdown-item aria-role="listitem" v-if="slides.length > 1"  @click="generateAllSlide()" class="lineButtonGenerate" >
            <w-icon class="iconButtonsGenerate" icon="animation-play-outline"/>
            <span class="labelButtonGenerate">{{$t('aiAvatar.generateAllSlide')}}</span>
          </b-dropdown-item>
        </b-dropdown>
      </div>
      <div class="lineButton" v-else>
        <w-button class="buttonOneSlide" :expanded="true" @click="generateSlide" icon="play-box-outline" :enabled="!!textToGenerate && !ssmlErrors"
                  :loading="isAvatarGenerating || loadingVoice"
                  color="primary" size="small">
          {{$t("aiAvatar.generate")}}
        </w-button>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import Vue from 'vue';
import {Component, Emit, Watch} from 'vue-property-decorator';
import store from '@/store';
import {Howl} from 'howler';
import VideoSource from '@/components/recorder/videoLayout/source/VideoSource.vue';
import WButton from '@/components/wrapper/w-button.vue';
import WIcon from '@/components/wrapper/w-icon.vue';
import WToolTip from '@/components/wrapper/w-toolTip.vue';
import WModal from '@/components/wrapper/w-modal.vue';
import CenterCenterPanel from '@/components/layout/CenterCenter.vue';
import WSwitch from '@/components/wrapper/w-switch.vue';
import WHelp from '@/components/wrapper/w-help.vue';
import CustomVBUploader from '@/components/recorder/sticker/buttons/CustomVBUploader.vue';
import {
  LIST_OF_AVATAR,
  Text2SpeachParams
} from "@/store/AIAvatar/aiavatarGenerationModel";
import {
  AVATAR_EDITING_TEXT,
  SELECT_AI_AVATAR,
  SELECT_AI_BACKGROUND,
  SELECT_AI_LANG, SELECT_AI_VOICE
} from "@/store/AIAvatar/AIAVatarAction";
import WSelect from "@/components/wrapper/w-select.vue";
import UpgradePlanButton from "@/components/upgradePlan/UpgradePlanButton.vue";
import WInput from "@/components/wrapper/w-input.vue";
import { escapeForSSML, listOfVoice, listOfVoicesLanguages } from "@/utils/voiceUtil";
import {  MAX_LENGTH_AIAVATAR_PREMIUM } from "@/utils/workspaceUtil";
import { PREVIEW_TEXT_2_SPEECH_GENERATE, TEXT_2_SPEECH_GENERATE } from "@/store/media/mediaAction";
import {base64ToBlob} from "@/utils/audioUtil";
import {Slide} from "@/store/slides2Video/slides2VideoModels";
import {clone} from "@/utils/cloneUtil";
import {updateCurrentSlide} from "@/utils/slides/slideUtil";
import WDivider from '@/components/wrapper/w-divider.vue';
import WError from '@/components/wrapper/w-error.vue';
import {checkValidSSML, generateErrorMessages} from '@/utils/ssmlUtil';
import {debounce} from 'lodash';
import { Segment } from "@/store/timeLine/timeEvent";


@Component({
  components: {
    WDivider,
    WError,
    WInput,
    UpgradePlanButton,
    WSelect,
    CustomVBUploader, WHelp, WSwitch, CenterCenterPanel, WModal, WToolTip, WIcon, WButton, VideoSource
  },
  computed: {},
})
export default class AIAvatarContentEditionPanel extends Vue {

  private playingVoice = false;
  private audioPlayer: Howl | null = null;
  private loadingVoice = false;
  private textToGenerate: string = "";
  private customPause: number = 2.5;
  private ssmlErrors: string = "";


  public mounted(){
    this.onCurrentSlideChange();
    this.checkVoiceGender();
  }

  public beforeDestroy() {
    // Cancel any pending debounced calls
    this.debouncedSSMLCheck.cancel();
    // Clean up audio player if needed
    this.stopPreviewVoice();
  }

  get isAvatarGenerating() {
    return store.getters.isAvatarGenerating
  }

  get isImageGenerationInProgress() {
    return store.getters.isImageGenerationInProgress
  }

  get selectedAvatar() {
    return store.getters.getAvatarSelected;
  }

  get slides(): Slide[] {
    return store.getters.slides;
  }

  get currentSlide(): Slide | null {
    if (store.getters.getCurrentSlideIndex > -1) {
      return store.getters.slides[store.getters.getCurrentSlideIndex];
    }
    return null;
  }
  get selectedSegment():Segment|null{
    return store.getters.getSelectedSegment
  }

  get langSelected() {
    return store.getters.getAvatarLangSelected || this.listOfVoicesLangs[0].value
  }

  get gender() {
    return LIST_OF_AVATAR.find((av) => {
      return av.avatar === this.selectedAvatar
    })?.gender
  }

  get listOfVoicesLangs() {
    const autoDetectOption = {label: this.$t('generic.autodetect'), value: null};
    return [autoDetectOption, ...listOfVoicesLanguages];
  }

  get listOfVoice() {
    return listOfVoice.filter((voice) => {
      return voice.gender === this.gender
    })
  }

  get voiceSelected() {
    return store.getters.getAvatarVoiceSelected
  }


  get maxlengthOfCharacter() {
    return MAX_LENGTH_AIAVATAR_PREMIUM;
    // if (this.isFreemium) {
    //   return MAX_LENGTH_AIAVATAR_FREEMIUM;
    // } else {
    //   return MAX_LENGTH_AIAVATAR_PREMIUM;
    // }
  }

  get isCustomPauseValid(): boolean {
    return this.customPause !== undefined && this.customPause > 0 && this.customPause <= 10;
  }

  /**
   * Validates SSML markup in the text-to-speech parameters
   *
   * @returns Promise resolving to a boolean indicating whether the SSML is valid
   */
  private async checkSSMLValidity(): Promise<boolean> {
    // Only proceed if there's a current slide and text has changed
    if (!this.currentSlide) return false;

    // Get validation errors from SSML checker
    const ssmlErrors = await checkValidSSML(escapeForSSML(this.textToGenerate));

    if (ssmlErrors.length === 0) {
      // No errors, so clear the error state
      this.ssmlErrors = "";
      return true;
    }

    // Only update if the error state actually changed
    const errorFormatted = generateErrorMessages(ssmlErrors);
    if (this.ssmlErrors !== errorFormatted) {
      this.ssmlErrors = errorFormatted;
    }

    return ssmlErrors.length === 0;
  }

  private debouncedSSMLCheck = debounce(() => {
    this.checkSSMLValidity();
  }, 1000); // debounce time in milliseconds

  private onAvatarLangChange(lang) {
    store.dispatch(SELECT_AI_LANG, lang)
    if(this.currentSlide){
      const newSlide=clone(this.currentSlide) as Slide;
      newSlide.lang=lang;
      updateCurrentSlide(newSlide);
    }
  }

  private onAvatarVoiceChange(voice) {
    store.dispatch(SELECT_AI_VOICE, voice)
    if(this.currentSlide){
      const newSlide=clone(this.currentSlide) as Slide;
      newSlide.voice=voice;
      updateCurrentSlide(newSlide);
    }
  }

  private onTextInput() {
    store.dispatch(AVATAR_EDITING_TEXT, this.textToGenerate);
    // update slides
    if(this.currentSlide){
      const newSlide=clone(this.currentSlide);
      newSlide.script=this.textToGenerate;
      updateCurrentSlide(newSlide);
    }
  }


  private goToPayment(){
    this.$router.push({name: 'MyWorkspacePayment'});
  }

  @Emit('generateSlide')
  private generateSlide() {
    return;
  }

  @Emit('generateAllSlide')
  private generateAllSlide() {
    return
  }

  @Watch("currentSlide")
  private onCurrentSlideChange() {
    if (this.currentSlide) {
      this.textToGenerate = this.currentSlide.script
      store.dispatch(AVATAR_EDITING_TEXT, this.currentSlide.script);
      if(this.currentSlide.lang){
        store.dispatch(SELECT_AI_LANG,this.currentSlide.lang)
      }
      // waiting the checkVoiceGender
      this.$nextTick(()=>{
        if(this.currentSlide?.voice){
          const isVoiceAvaialbleForGender=!!this.listOfVoice.find((v)=>v.value===this.voiceSelected)
          if(isVoiceAvaialbleForGender) {
            store.dispatch(SELECT_AI_VOICE, this.currentSlide.voice)
          }else{
            store.dispatch(SELECT_AI_VOICE,this.listOfVoice[0].value)
          }
        }
      })

    } else {
      this.textToGenerate = "";
    }
    store.dispatch(AVATAR_EDITING_TEXT, this.textToGenerate);
    this.debouncedSSMLCheck();
  }

  @Watch("selectedAvatar")
  private onAvatarChange(){
    this.checkVoiceGender()
  }

  private checkVoiceGender(){
    const isVoiceAvaialbleForGender=!!this.listOfVoice.find((v)=>v.value===this.voiceSelected)
    if(!isVoiceAvaialbleForGender) {
      store.dispatch(SELECT_AI_VOICE, this.listOfVoice[0].value)
      if(this.currentSlide){
        const newSlide=clone(this.currentSlide) as Slide;
        newSlide.voice=store.getters.getAvatarVoiceSelected;
        updateCurrentSlide(newSlide);
      }
    }
  }

  /***
   ** PUBLIC METHODS
   **/

  // Listening the voice
  public async previewVoice() {
    if (this.playingVoice) {
      this.stopPreviewVoice();
      return;
    } else {
      this.stopPreviewVoice();
    }

    // STOP
    if (this.audioPlayer) {
      this.audioPlayer.stop();
      this.audioPlayer = null;
      store.dispatch(PREVIEW_TEXT_2_SPEECH_GENERATE,false);
      this.playingVoice = false;
      return;
    }
    this.loadingVoice = true;
    const text2SpeachParams = new Text2SpeachParams()
    text2SpeachParams.text = this.textToGenerate;
    text2SpeachParams.voice = this.voiceSelected;
    text2SpeachParams.lang = this.langSelected;
    try {
      const base64 = await store.dispatch(TEXT_2_SPEECH_GENERATE, text2SpeachParams);
      const url = URL.createObjectURL(base64ToBlob(base64, 'audio/mp3'));
      this.loadingVoice = false;
      this.audioPlayer = new Howl({
        src: [url],
        autoplay: true,
        html5: true,
        onend: () => {
          store.dispatch(PREVIEW_TEXT_2_SPEECH_GENERATE,false);
          this.playingVoice = false;
        },
        volume: 1,
      });
      this.audioPlayer.play();
      this.playingVoice = true;
      store.dispatch(PREVIEW_TEXT_2_SPEECH_GENERATE,true);
    } catch (e: unknown) {
      this.ssmlErrors = e instanceof Error ? e.message : String(e);
      this.loadingVoice = false;
    }
  }


  // Stoping the preview voice
  public async stopPreviewVoice() {

    if (this.audioPlayer) {
      this.audioPlayer.stop();
      this.audioPlayer = null;
      this.playingVoice = false;
      store.dispatch(PREVIEW_TEXT_2_SPEECH_GENERATE,false);

    }
  }

  public async getAudioInformationForText(): Promise<{ seconds: number, blob: Blob }> {
    return new Promise(async (resolve, reject) => {
      this.stopPreviewVoice();
      const text2SpeachParams = new Text2SpeachParams()
      // add pause to avoid the end of the voice too early for avatar (LATENT_SYNC_ISSUE)
      text2SpeachParams.text = this.textToGenerate+"<break time='0.4s'/>";
      text2SpeachParams.voice = this.voiceSelected;
      text2SpeachParams.lang=this.langSelected
      try {
        const base64 = await store.dispatch(TEXT_2_SPEECH_GENERATE, text2SpeachParams);
        const blob = base64ToBlob(base64, 'audio/mp3');
        const url = URL.createObjectURL(blob);
        this.audioPlayer = new Howl({
          src: [url],
          autoplay: true,
          html5: true,
          onload: () => {
            this.audioPlayer.stop();
            resolve({seconds: this.audioPlayer.duration(), blob: blob});
          },
          onend: () => {
            this.playingVoice = false;
          },
          volume: 0,
        });
        this.audioPlayer.play();
      } catch (e) {
        this.ssmlErrors = e instanceof Error ? e.message : String(e);
        this.$emit("errorDuringProcess", this.ssmlErrors);
      }
    })
  }

  public insertPauseTag(value: number): void {
    // Get textarea element
    const textarea = this.$el.querySelector('.contentTextAreaAI textarea') as HTMLTextAreaElement;

    if (textarea) {
      // Get current cursor position
      const cursorPos = textarea.selectionStart;

      // Get text before and after cursor
      const textBefore = this.textToGenerate.substring(0, cursorPos);
      const textAfter = this.textToGenerate.substring(cursorPos);

      // Insert pause tag at cursor position
      const pauseTag = `<break time='${value}s'/>`;
      this.textToGenerate = textBefore + pauseTag + textAfter;

      // Set cursor position after the inserted tag
      this.$nextTick(() => {
        textarea.focus();
        textarea.setSelectionRange(cursorPos + pauseTag.length, cursorPos + pauseTag.length);
        this.customPause = 2.5;

        // Trigger the input event to update any bindings
        this.onTextInput();
      });
    }
  }
}
</script>
<style lang="scss">
#buttonDropDown {
  width: 100%;
  .dropdown-trigger {
    width: 100%;
  }
}
// no scope to manage textarea size
.contentTextAreaAI {
  .textarea {
    min-height: 288px;
    height: 288px;
    //resize: none;
  }
  &.overtext{
    .help{
      color: red;
    }
  }
}
</style>
<style lang="scss" scoped>
.avatarContentEditionPanel {
  width: 100%;
  height: 100%;


  .avatarContentEditionContainer {
    align-items: center;
    text-align: left;
    font-weight: 700;
    color: var(--black);

    .avatarContentEditorTitle {
      color: var(--black);
      font-weight: 900;
      font-size: 20px;
      margin-bottom: 8px;
      display: flex;
      justify-content: space-between;
    }

    .pauseButtonContent {
      color: var(--black);
      font-weight: 600;
      font-size: 14px;
      margin-bottom: 8px;
      display: flex;
    }

    .lineButton{
      margin-top: 8px;
      .buttonOneSlide{
        margin-right: 8px;
        margin-bottom: 16px;
      }
      .lineButtonGenerate {
        display: flex;
        align-items: center;
        .iconButtonsGenerate {
          margin-right: 8px;
        }
        .labelButtonGenerate{
          margin-right: 8px;
        }
        &.disabledFreemium{
          .labelButtonGenerate{
            cursor: not-allowed;
            opacity: 0.3;
          }
        }
      }
    }

    .errorSSML {
      margin-top: 16px;
    }

    .lineSelect {
      display: flex;
      align-items: center;
      justify-content: flex-start;
      margin-top: 16px;
      .labelSelect {
        position: relative;
        text-align: left;
        font-size: 14px;
        color: var(--black);
        font-weight: 700;
        justify-content: space-between;
        align-items: flex-end;
        display: flex;
        margin-right: 16px;
        min-width: 90px;
      }
      .langSelector, .voiceSelector {
        flex:1;
      }
      .buttonPlayPreview {
        align-content: center;
        margin-left: 8px;
        margin-bottom: 12px;
        cursor: pointer;
      }

    }



    .upgradeMessage {
      font-size: 14px;
      display: flex;
      padding-right: 0px;
      color: var(--light2);
    }


    .contentTextAreaAI {
      margin-bottom: -16px;
    }

    .buttonValidateStep {
      position: absolute;
      bottom: 16px;
      right: 16px;
    }
  }

  .pauseCustomTitle {
    font-weight: 700;
  }

  .pauseCustomLine {
    display: flex;
    gap: 8px; /* Adds space between the input and button */
    width: 100%;
    margin-top: 8px; /* Adds some space from the text above */
  }
}
</style>
